import './style.scss';
import { Tabs } from 'views/components/atoms/tabs';
import { PageLocationSP } from 'views/components/atoms/pageLocation';
import { formatNumber } from 'views/utils/format/FormatNumber';
import { formatDateToSlashDateTime } from 'views/utils/format/FormatDate';
import { AssetInfo } from './Atoms/AssetIInfo';
import { WithdrawButton } from './Atoms/WithdrawButton';
import { WithdrawCancelButton } from './Atoms/WithdrawCancelButton';
import { useAccountAsset } from './hook';
import { WithdrawModal } from './Modal/WithdrawModal';
import { WithdrawalCancelModal } from './Modal/WithdrawalCancelModal';
import { WithdrawalRequestCompleteModal } from './Modal/WithdrawalRequestCompleteModal';
import { GrayBorder } from 'views/components/atoms/separator';
import { Button, ButtonSize, FillType } from 'views/components/atoms/button';
import { Toast } from 'views/components/atoms/toast';

const formatAssetAmount = (amount: number | undefined) => (amount === undefined ? '-' : formatNumber(amount));

const formatSubtractedAssetAmount = (amount: number | undefined, commission: number | undefined) =>
  amount === undefined || commission === undefined ? '-' : formatNumber(amount - commission);

export const AccountAsset = () => {
  const {
    accountAsset: { DepositAmount, MarginAmount, WithdrawableAmount, TotalAssets, UpdTime, WithdrawalDay },
    reservationList,
    modalProps,
    withdrawalCancelModalProps,
    withdrawalRequestCompleteModalProps,
    withdrawAmount,
    setWithdrawAmount,
    withdrawAmountError,
    isWithdrawAmountError,
    onWithdrawAmountBlur,
    requestWithdrawalCheck,
    isRequestingWithdrawal,
    cancelWithdrawalCheck,
    isCancelingWithdrawal,
    refreshAccountAsset,
    isUpdatingAccountAsset,
    toastProps,
  } = useAccountAsset();

  const assetWithdraw = () => {
    return (
      <div className="accountAssetWrapper">
        <div className="assetInfoWrapper">
          <div className="assetInfoWrapper_title">
            お預り資金（更新日：{UpdTime}）
            <div className="assetInfoWrapper_title_annotation">※お預り金は1時間に1回程度の間隔で更新されます</div>
          </div>
          <div className="assetInfoWrapper_asset">
            <AssetInfo labelStyle="_red" label="お預り金" amount={formatNumber(DepositAmount)} />
          </div>
          <div className="assetInfoWrapper_asset assetInfoWrapper_asset_detailEndline">
            <AssetInfo
              label="申込証拠金"
              detail="（募集中銘柄の購入用としてお預りしている資金）"
              amount={formatNumber(MarginAmount)}
            />
          </div>
          <div className="assetInfoWrapper_asset">
            <AssetInfo label="お預り資金合計" amount={formatNumber(TotalAssets)} />
            <div className="assetInfo_label_black">お預り資金合計 = お預り金 + 申込証拠金</div>
          </div>
        </div>
        <GrayBorder />

        <div className="assetInfoWrapper">
          <div className="assetInfoWrapper_title assetInfoWrapper_titleWithAction">
            <span>出金予約状況</span>
            <div className="assetInfoWrapper_titleAction">
              <Button
                label={isUpdatingAccountAsset ? '更新中' : '出金余力を更新'}
                fillType={FillType.primaryFill}
                buttonSize={ButtonSize.sizeS}
                onClick={() => refreshAccountAsset()}
                disabled={isUpdatingAccountAsset}
              />
            </div>
          </div>
          <div className="assetInfoWrapper_asset">
            <AssetInfo label="出金余力" amount={formatNumber(WithdrawableAmount)} />
            <div className="assetInfo_label_black">
              出金余力 = <div className="assetInfo_label_red">お預り金</div> - 出金予定額 （出金予約 + 未配分返金）
            </div>
          </div>
          <div className="assetInfoWrapper_asset">
            {reservationList.map((item) => (
              <>
                <AssetInfo
                  label={`出金予定額（${item.type}）${item.type == '出金予約' ? '' : '※自動'}`}
                  amount={formatAssetAmount(item.reservation)}
                  date={item.reservation ? `(${formatDateToSlashDateTime(item.withdrawDate, false)})` : undefined}
                />
                <div className="reservationDetail">
                  <div className="reservationDetail_uchiwake">【内訳】</div>
                  <div className="reservationDetail_amount">
                    <AssetInfo
                      label="振込予定額"
                      amount={formatSubtractedAssetAmount(item.reservation, item.commission)}
                    />
                    <AssetInfo label="振込手数料" amount={formatAssetAmount(item.commission)} />
                  </div>
                </div>
              </>
            ))}
          </div>

          <div className="assetInfoWrapper_asset assetInfoWrapper_asset_WithdrawalReservation">
            <div className="withdrawActionRow">
              <div className="withdrawActionRow_label">
                出金予約額入力{WithdrawalDay && `(${formatDateToSlashDateTime(WithdrawalDay, false)})`}
              </div>
              <WithdrawButton
                withdrawAmount={withdrawAmount}
                setWithdrawAmount={setWithdrawAmount}
                isError={isWithdrawAmountError}
                errorText={withdrawAmountError}
                onBlur={onWithdrawAmountBlur}
                onClick={requestWithdrawalCheck}
                disabled={isRequestingWithdrawal || isCancelingWithdrawal}
              />
            </div>
            <div className="assetInfoWrapper_withdrawAnnotation">
              <div>
                ※お預りしている資金は、出金余力に表示されている金額の範囲内での出金が可能です。出金いただく金額を「出金予約額」にご入力ください。
              </div>
              <div>
                ※出金予約額の入力は、毎週金曜日11時～木曜日12時まで入力が可能です（毎週木曜日12時から金曜日11時までは入力不可となります）
              </div>
              <div>
                ※毎週木曜日の12時までに入力いただいた金額を、翌金曜日にご登録いただいている「指定預金口座」へお振込みいたします。
              </div>
              <div>
                （木曜日あるいは金曜日が休日の場合は前営業日にスライドします。スライドした振込日の前営業日12時が入力の締切日になります）
              </div>
              <div>
                ※1度入力された「出金予約額」を変更する場合は、変更後の金額を再度を「出金予約額」にご入力ください。
              </div>
              <div>※実際に指定預金口座に振込する金額は、所定の振込手数料を控除した金額になります。</div>
              <div></div>
            </div>
          </div>
          <div className="assetInfoWrapper_asset">
            <div className="withdrawActionRow">
              <div className="withdrawActionRow_label">出金予約取消</div>
              <WithdrawCancelButton
                onClick={cancelWithdrawalCheck}
                disabled={isRequestingWithdrawal || isCancelingWithdrawal}
              />
            </div>
          </div>
        </div>
        <WithdrawModal {...modalProps} />
        <WithdrawalCancelModal {...withdrawalCancelModalProps} />
        <WithdrawalRequestCompleteModal {...withdrawalRequestCompleteModalProps} />
        <Toast {...toastProps} />
      </div>
    );
  };

  return (
    <section className="outletWrapper">
      <div className="centerContent">
        <PageLocationSP />
        <Tabs
          tabLists={[{ title: 'お預り資金・出金' }]}
          tabContents={[assetWithdraw()]}
          selectedIndex={0}
          onSelect={(index) => {}}
        />
      </div>
    </section>
  );
};
